"use client";

import { Icon, ospThemes } from "@ospweb/sdk-ui";
import React, { useCallback, useState } from "react";

import log from "logbeacon";
import { useShellRouter } from "@/src/hooks";
import { AddAssetsButton } from "./AddAssetsButton";
import { DeleteConfirmModal } from "./DeleteConfirmModal";
import { PortfolioMenuButton } from "./PortfolioMenuButton";
import { TodayGain } from "./TodayGain";
import { ALL_PORTFOLIO_OPTION_ID } from "@/src/components/PortfolioContainer/constants";
import { EditPortfolioModalCreateAble } from "@/src/components/EditPortfolioModal";
import { FolderInfo } from "@/src/store/favorite/types";
import { MainHeader } from "@/src/components";
import { PortfolioSwitch } from "@/src/components/PortfolioSwitch/PortfolioSwitch";
import { TokenResultWithType } from "@/src/hooks/usePortfolioAssetsInfinite";
import { computeTodayGain } from "@/src/utils";
import { usePortfolioContext } from "@/src/components/Portfolio/PortfolioContext";
import { runShellAwarePageBack } from "@/src/lib/bridge/shellAppNavigation";
import { ROUTER_PATH } from "@/src/config";
import { getBridge } from "@/src/lib/bridge";

interface PortfolioHeaderProps {
  portfolioId: string;
  marketData: TokenResultWithType[];
  currentFolder: FolderInfo;
}

export const PortfolioHeader: React.FC<PortfolioHeaderProps> = ({
  portfolioId,
  marketData,
  currentFolder,
}) => {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const router = useShellRouter();
  const { searchAsset } = usePortfolioContext();

  const handleRequestDelete = useCallback(() => {
    setIsDeleteOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    log.debug("[文案]Confirm delete portfolio from header:", portfolioId);
    // const result = await favoritesCenter?.deleteFolder?.(portfolioId);
    // if (result.success) {
    //   setIsDeleteOpen(false);
    //   router.replace(
    //     `${ROUTER_PATH.PORTFOLIO}/${tab}/${ALL_PORTFOLIO_OPTION_ID}`,
    //   );
    // }
  }, [portfolioId]);

  // 处理编辑投资组合
  const handleRequestEdit = useCallback(async () => {
    log.debug("[文案]Edit portfolio from header:", portfolioId);
    await EditPortfolioModalCreateAble.call({
      portfolioId: portfolioId,
      initialValue: currentFolder?.name,
    });
  }, [currentFolder?.name, portfolioId]);

  const handleBack = useCallback(() => {
    if (getBridge().isInApp()) {
      runShellAwarePageBack(router);
      return;
    }
    const mainPath = sessionStorage.getItem("mainPath");
    const currHref = window.location.href;
    const shouldBack = mainPath !== currHref;

    if (shouldBack) {
      router.back();
    } else {
      router.push(ROUTER_PATH.HOME);
    }
  }, [router]);

  const isAllPortfolio = portfolioId === ALL_PORTFOLIO_OPTION_ID;

  const todayGain = computeTodayGain(marketData);

  return (
    <>
      {/* 移动端头部 */}
      <div className="w-full flex md:hidden h-[52px]">
        <MainHeader
          isBack
          className="w-full flex md:hidden"
          rightChildren={
            <div className="flex gap-3 items-center mr-3">
              {/* Add Assets 按钮 */}
              <AddAssetsButton />
              {/* 管理菜单 */}
              {isAllPortfolio ? (
                <></>
              ) : (
                <PortfolioMenuButton
                  onEdit={handleRequestEdit}
                  onDelete={handleRequestDelete}
                />
              )}
            </div>
          }
          leftChildren={
            <div className="flex items-center">
              <Icon
                data-openreplay-label="left-arrow-alt"
                className="mr-2"
                name="left-arrow-alt"
                size={24}
                color={ospThemes.colors.content_primary}
                onClick={handleBack}
              />
              <div className="flex flex-col flex-1 min-h-0 min-w-0 max-w-[calc(100vw-180px)]">
                {/* Portfolio选择器区域 */}
                <PortfolioSwitch />
                {/* Today's Gain显示区域 - 仅在选择portfolio时显示 */}
                {isAllPortfolio || !!searchAsset ? (
                  <></>
                ) : (
                  <TodayGain todayGain={todayGain} className="" />
                )}
              </div>
            </div>
          }
        />
      </div>

      {/* 桌面端头部 */}
      <div
        className="hidden md:flex md:items-center md:justify-between pb-6 pt-8"
        data-testid="portfolio-header"
      >
        {/* 左侧区域：标题和收益显示 */}
        <div className="flex flex-col gap-[4px] min-h-0 min-w-0">
          {/* Portfolio选择器区域 */}
          <PortfolioSwitch />
          {/* Today's Gain显示区域 - 仅在选择portfolio时显示 */}
          {isAllPortfolio || !!searchAsset ? (
            <></>
          ) : (
            <TodayGain todayGain={todayGain} />
          )}
        </div>
        {/* 右侧区域：操作按钮 */}
        <div className="flex gap-4 items-center">
          {/* Add Assets 按钮 */}
          <AddAssetsButton />

          {/* 管理菜单 */}
          {isAllPortfolio ? (
            <></>
          ) : (
            <PortfolioMenuButton
              onEdit={handleRequestEdit}
              onDelete={handleRequestDelete}
            />
          )}
        </div>
      </div>

      {/* 删除确认弹窗 */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};
